import TableCell from '../../Utilities/TruncatedTableCell';
import TableRow from "@mui/material/TableRow";
import dayjs from "dayjs";
import { useContext } from "react";
import RankOrderRowViewModel from "../ViewModels/RankOrderRowViewModel";
import { useLocation, useNavigate } from "react-router";
import { navigateToSite } from "../../Site/SiteNavigation";
import { SessionContext } from "../../Contexts/SessionContext";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";

type RankOrderColumn = 'rank' | 'reference' | 'description' | 'town' | 'gfa' | 'dayOfWeek' | 'date' | 'movement';

interface IProps {
    row: RankOrderRowViewModel;
    rank: number;
    percentile15Rank: number | null;
    percentile85Rank: number | null;
    type: string;
    clickToggle: string;
    searchValue: string;
    isGfa: boolean;
    columnSx: Record<RankOrderColumn, SxProps<Theme>>;
}

export default function RankOrderRow({ row, rank, percentile15Rank, percentile85Rank, type, clickToggle, searchValue, isGfa, columnSx }: IProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { sessionState } = useContext(SessionContext);

    const formatToThreeDecimals = (value: number | null): string => {
        return (value ?? 0).toFixed(3);
    };

    const resolveSurveyRef = () => {
        const matchingSurvey = sessionState.selectedSitesAndSurveys.find((survey) =>
            survey.siteActive &&
            survey.surveyActive &&
            survey.siteRef === (row.siteRef ?? '') &&
            dayjs(survey.date).isSame(dayjs(row.date), 'day')
        );

        return matchingSurvey?.surveyRef ?? row.siteRef ?? '';
    };

    const navigation = () => {
        if (clickToggle === 'site')
            navigateToSite(navigate, location, {
                siteRef: row.siteRef ?? '',
                type,
                fromTab: 'rankTable',
                returnLabel: 'Back to Rank Order'
            });
        else
            navigate(`/triprate/results?selectedSurvey=${encodeURIComponent(resolveSurveyRef())}&fromTab=rankTable`);
    };

    const highlightText = (text: string) => {
        if (!searchValue) return text;
        const regex = new RegExp(`(${searchValue})`, 'gi');
        return text.split(regex).map((part, i) =>
            regex.test(part) ? <span key={i} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
        );
    };

    const match = (value: any) => (typeof value === 'string' ? highlightText(value) : value);

    const color = (rank === percentile15Rank || rank === percentile85Rank) ? 'lightblue' : 'inherit';
    console.log(color, rank)
    return (
        <TableRow
            onClick={navigation}
            sx={{
                // this fixed an issue where the color was not applying, this now applys directly to the cell rather than through mui
                '& > *': { 
                    backgroundColor: color,
                },
            }}
        >
            <TableCell sx={columnSx.rank}>{match(rank.toString())}</TableCell>
            <TableCell sx={columnSx.reference}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>{match(row.siteRef)}</Typography>
            </TableCell>
            <TableCell sx={columnSx.description}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>
                    {match(row.description)}
                </Typography>
            </TableCell>
            <TableCell sx={columnSx.town}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>{match(row.location)}</Typography>
            </TableCell>
            <TableCell sx={isGfa ? columnSx.gfa : undefined}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>{match(row.parameter)}</Typography>
            </TableCell>
            <TableCell sx={columnSx.dayOfWeek}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>{match(row.day)}</Typography>
            </TableCell>
            <TableCell sx={columnSx.date}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>
                    {match(dayjs(row.date).format('DD/MM/YYYY'))}
                </Typography>
            </TableCell>
            {row.peakHour != null && <TableCell>{match(row.peakHour)}</TableCell>}
            <TableCell sx={columnSx.movement}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>
                    {match(formatToThreeDecimals(row.arrivals))}
                </Typography>
            </TableCell>
            <TableCell sx={columnSx.movement}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>
                    {match(formatToThreeDecimals(row.departures))}
                </Typography>
            </TableCell>
            <TableCell sx={columnSx.movement}>
                <Typography component="span" noWrap sx={{ display: 'block' }}>
                    {match(formatToThreeDecimals(row.totals))}
                </Typography>
            </TableCell>
            {row.parkingSpacesPerDwelling != null && (
                <TableCell>{match(row.parkingSpacesPerDwelling?.toFixed(2))}</TableCell>
            )}
        </TableRow>
    );
}
